import { jsonFormToDefaultValues } from '@europrocurement/flexy-form';
import {
    CatalogApiObject,
    CatalogsFull,
    CollectionDeDossierApi,
    Contact,
    FormulaireFormaliteApi,
    Prescripteur,
    SousClientApi,
} from '@europrocurement/l2d-domain';
import {
    type FproPrestationJsonldCollectiondossierCreated,
    type FproPrestationJsonldDossierReadNoteGroupTimestampableBlameambleGroup,
    PrestationApi,
    type PrestationCreatePrestationDtoJsonldPrestationCreate,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';

export type createCollectionFormaliteProps = {
    prescripteur: Prescripteur;
    contact: Contact;
    // idFormalite: Array<number>;
    // idCatalogs: Array<number>;
    catalogs?: CatalogApiObject[];
    collectionApi: CollectionDeDossierApi;
    formulairesApi: FormulaireFormaliteApi;
    xIdSociete: number;
    libelle?: string;
    sousClient?: SousClientApi;
    sousClientOpere?: SousClientApi;
    infoComp?: string;
    societeRef?: string;
    societeDenomination?: string;
};

const catalogToPrestation = (
    catalog: CatalogApiObject,
    formulaire: string,
    resetStatus: boolean,
): PrestationCreatePrestationDtoJsonldPrestationCreate => {
    if (!catalog['@id']) {
        throw new Error('missing catalog');
    }
    if (!catalog.package) {
        throw new Error('missing package');
    }
    if (!catalog.package.formalite) {
        throw new Error('missing formalite');
    }
    if (!catalog.package.formalite['@id']) {
        throw new Error('missing formalite');
    }

    return {
        libelle: catalog.nomCommercial as string,
        deleted: false,
        catalogue: catalog['@id'].replace('/offre/catalogues', '/formalite/catalogues'),
        formalite: catalog.package.formalite['@id'].replace(
            '/offre/formalite',
            '/formalite/formalites',
        ),
        json: formulaire,
        resetStatus,
    };
};

export const getPrestationsPayload = async (
    catalogs: Array<CatalogApiObject>,
    formulairesApi: FormulaireFormaliteApi,
    resetStatus: boolean,
): Promise<Array<PrestationCreatePrestationDtoJsonldPrestationCreate>> => {
    const formsBuffer = new Map();
    const formulairePromises = catalogs
        .map((catalog) => (catalog as CatalogsFull)?.package?.formalite?.formulaire?.id)
        .filter((formulaireId) => formulaireId !== undefined)
        .map((formulaireId) =>
            formulairesApi.apiFormulaireFormalitesIdGet({ id: formulaireId as unknown as string }),
        );

    (await Promise.all(formulairePromises)).forEach((r) => {
        const { data } = r;
        const formulaireId = data?.id as number;
        const rawForm = data.formulaire;
        formsBuffer.set(formulaireId, JSON.parse(rawForm));
    });

    return catalogs.map((catalog) => {
        const formulaireId = (catalog as CatalogsFull).package?.formalite?.formulaire?.id;
        let formulaire = '{}';

        if (formulaireId && formsBuffer.has(formulaireId as number)) {
            const t = jsonFormToDefaultValues(formsBuffer.get(formulaireId as number));
            formulaire = JSON.stringify(t);
        }

        return catalogToPrestation(catalog, formulaire, resetStatus);
    });
};

export const createCollectionFormalite = async ({
    prescripteur,
    contact,
    catalogs,
    collectionApi,
    formulairesApi,
    xIdSociete,
    libelle,
    sousClient,
    sousClientOpere,
    infoComp,
    societeRef,
}: createCollectionFormaliteProps) => {
    if (!prescripteur.id) {
        throw new Error('missing prescriber');
    }

    if (!catalogs || catalogs.length === 0) {
        throw new Error('missing catalog');
    }
    if (!sousClient || !sousClient.id) {
        throw new Error('missing sousClient');
    }

    const prestations = await getPrestationsPayload(catalogs, formulairesApi, false);

    const res = await collectionApi.apiCollectionsDossiersPost({
        collectionDeDossierJsonldCollectiondossierCreate: {
            dossiers: [
                {
                    contact: contact.id,
                    societeRef,
                    societeDenomination: sousClient.raisonSociale,
                    origine: 13,
                    // "idGreffe": 106,
                    societeSiren: sousClient.siren,
                    clientOpere: sousClientOpere ? sousClientOpere.id : sousClient.id,
                    idFormeJuridique: 21,
                    // "acompteAttendu": "500.00",
                    // "affaire": "affaire du dossier",
                    prestations,
                },
            ],
            prescripteur: `/formalite/tiers/${prescripteur.id}`,
            client: `/formalite/tiers/${sousClient.id}`,
            societe: xIdSociete,
            libelle,
            infoComp,
        },
    });

    if (!res.data) {
        throw new Error('missing collection');
    }

    return res.data;
};

export const updatePrescriptionCollection = async (
    idCollection: number | string,
    collectionApi: CollectionDeDossierApi,
    prescripteur: number | string,
) => {
    const freshCollection = (
        await collectionApi.apiCollectionsDossiersIdGet({ id: `${idCollection}` })
    ).data;
    if (!freshCollection || !freshCollection.id) {
        throw new Error('error in Collection');
    }
    await collectionApi.apiCollectionsDossiersIdPatch({
        id: `${idCollection}`,
        collectionDeDossierCollectiondossierUpdate: {
            client: `/formalite/tiers/${prescripteur}`,
            typeFacturation: freshCollection.typeFacturation,
            infosSurFacture: freshCollection.infosSurFacture,
        },
    });
};

export const addDataToCollection = async (
    idCollection: number | string,
    collectionApi: CollectionDeDossierApi,
    data: string,
) => {
    const freshCollection = (
        await collectionApi.apiCollectionsDossiersIdGet({ id: `${idCollection}` })
    ).data;
    if (!freshCollection || !freshCollection.id) {
        throw new Error('error in Collection');
    }
    await collectionApi.apiCollectionsDossiersIdPut({
        id: `${idCollection}`,
        collectionDeDossierJsonldCollectiondossierUpdate: {
            ...freshCollection,
            // prescripteur: freshCollection.prescripteur?.replace('/tiers/tiers', '/formalite/tiers'),
            // client: freshCollection.client?.replace('/tiers/tiers', '/formalite/tiers'),
            infoComp: data,
        },
    });
};

export const updateJsonPrestationFormalite = async (
    prestationFormaliteApi: PrestationApi,
    presta:
        | FproPrestationJsonldDossierReadNoteGroupTimestampableBlameambleGroup
        | FproPrestationJsonldCollectiondossierCreated
        | null,
    json: string,
): Promise<string> => {
    if (!presta?.id) {
        throw new Error('missing presta');
    }

    if (!presta.catalogue) {
        throw new Error('no catalog in presta');
    }

    const mergedJson = JSON.stringify({
        ...JSON.parse(presta.json || '{}'),
        ...JSON.parse(json),
    });

    await prestationFormaliteApi.apiPrestationsIdPut({
        id: `${presta.id}`,
        prestationJsonldPrestationUpdate: {
            libelle: presta.libelle,
            json: mergedJson,
        },
    });

    return mergedJson;
};
